/**
 * Customer-scoped sight tape bow profiles + version history (localStorage).
 * Requires a logged-in customer id from Liquid (data-stc-customer-id).
 */
(function (global) {
  'use strict';

  var STORAGE_PREFIX = 'titlex_stc_library_v1_';
  var PENDING_KEY = 'tx_stc_pending_snapshot';
  var CUSTOMER_COOKIE = 'tx_stc_customer';
  var REAUTH_ATTEMPT_KEY = 'tx_stc_reauth_attempted';
  var COOKIE_MAX_AGE_MS = 90 * 86400000;
  var MAX_PROFILES = 12;
  var MAX_HISTORY = 20;

  function readCustomerCookie() {
    var match = document.cookie.match(new RegExp('(?:^|; )' + CUSTOMER_COOKIE + '=([^;]*)'));
    if (!match) return null;
    try {
      var data = JSON.parse(decodeURIComponent(match[1]));
      if (!data || !data.id) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeCustomerCookie(data) {
    if (!data || !data.id) return;
    try {
      var payload = {
        id: String(data.id),
        email: data.email ? String(data.email) : '',
        at: data.at || Date.now(),
      };
      var exp = new Date(Date.now() + COOKIE_MAX_AGE_MS).toUTCString();
      var secure = location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        CUSTOMER_COOKIE +
        '=' +
        encodeURIComponent(JSON.stringify(payload)) +
        '; path=/; expires=' +
        exp +
        '; SameSite=Lax' +
        secure;
    } catch (e) {
      /* blocked */
    }
  }

  function clearCustomerCookie() {
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = CUSTOMER_COOKIE + '=; path=/; Max-Age=0; SameSite=Lax' + secure;
  }

  function applyAuthToRoot(root, loggedIn, customerId, email, optimistic) {
    if (!root) return;
    root.setAttribute('data-stc-logged-in', loggedIn ? '1' : '0');
    if (loggedIn && customerId) {
      root.setAttribute('data-stc-customer-id', String(customerId));
      if (email) root.setAttribute('data-stc-customer-email', email);
      else root.removeAttribute('data-stc-customer-email');
    } else {
      root.removeAttribute('data-stc-customer-id');
      root.removeAttribute('data-stc-customer-email');
    }
    if (optimistic) root.setAttribute('data-stc-auth-optimistic', '1');
    else root.removeAttribute('data-stc-auth-optimistic');
    var link = root.querySelector('[data-calc-presets-link]');
    if (link) link.setAttribute('data-cp-logged-in', loggedIn ? '1' : '0');
  }

  function syncCookieFromRoot(root) {
    if (!root || root.getAttribute('data-stc-logged-in') !== '1') return;
    var id = root.getAttribute('data-stc-customer-id');
    if (!id) return;
    writeCustomerCookie({
      id: id,
      email: root.getAttribute('data-stc-customer-email') || '',
    });
  }

  function applyOptimisticAuthFromCookie(root) {
    if (!root || root.getAttribute('data-stc-logged-in') === '1') return false;
    var cookie = readCustomerCookie();
    if (!cookie || !cookie.id) return false;
    applyAuthToRoot(root, true, cookie.id, cookie.email || '', true);
    return true;
  }

  function markStaleSession(root, cookie) {
    if (!root) return;
    root.setAttribute('data-stc-stale-session', '1');
    applyAuthToRoot(root, false, null, null, false);
    if (cookie && cookie.email) {
      var emailEl = root.querySelector('[data-stc-reauth-email]');
      var wrapEl = root.querySelector('[data-stc-reauth-email-wrap]');
      if (emailEl) emailEl.textContent = cookie.email;
      if (wrapEl) wrapEl.hidden = false;
    }
  }

  function clearStaleSession(root) {
    if (!root) return;
    root.removeAttribute('data-stc-stale-session');
  }

  function uid() {
    return 'stc_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  function readSeg(root, attr) {
    var btn = root.querySelector('[' + attr + '][aria-pressed="true"]');
    return btn ? btn.getAttribute(attr) : null;
  }

  function num(v) {
    if (v == null || v === '') return null;
    var n = Number(v);
    return isFinite(n) ? n : null;
  }

  function val(root, sel) {
    var el = root.querySelector(sel);
    return el ? el.value : '';
  }

  function setSeg(root, attr, value) {
    if (!root || !attr || value == null) return;
    root.querySelectorAll('[' + attr + ']').forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute(attr) === String(value) ? 'true' : 'false');
    });
  }

  function setInput(root, sel, value) {
    var el = root.querySelector(sel);
    if (!el || value == null || value === '') return;
    el.value = String(value);
  }

  function storageKey(customerId) {
    return STORAGE_PREFIX + String(customerId);
  }

  function readRaw(customerId) {
    if (!customerId) return null;
    try {
      return localStorage.getItem(storageKey(customerId));
    } catch (e) {
      return null;
    }
  }

  function writeRaw(customerId, json) {
    if (!customerId) return false;
    try {
      localStorage.setItem(storageKey(customerId), json);
      return true;
    } catch (e) {
      return false;
    }
  }

  function defaultProfile(name) {
    return {
      id: uid(),
      name: name || 'My bow',
      updatedAt: Date.now(),
      snapshot: null,
      history: [],
    };
  }

  function emptyLibrary() {
    var hunting = defaultProfile('Hunting bow');
    var target = defaultProfile('Target bow');
    return {
      v: 1,
      activeProfileId: hunting.id,
      profiles: [hunting, target],
    };
  }

  function migrateFromCalcPresets() {
    var P = global.TitleXCalcPresets;
    if (!P || !P.load) return null;
    var data = P.load();
    if (!data) return null;
    var profile = defaultProfile(data.name || 'My setup');
    profile.snapshot = {
      fps: P.resolveFps ? P.resolveFps(data) : null,
      grains: data.arrow && data.arrow.grains,
      gap: data.sightTape && data.sightTape.gap2060,
      startYd: data.sightTape && data.sightTape.startYd,
      endYd: data.sightTape && data.sightTape.endYd,
      tip: (data.sightTape && data.sightTape.tip) || (data.arrow && data.arrow.tip) || 'field',
      tickStep: data.sightTape && data.sightTape.tickStep,
      align: data.sightTape && data.sightTape.align,
      tickAlign: data.sightTape && data.sightTape.tickAlign,
      labelOffsetPx: data.sightTape && data.sightTape.labelOffsetPx,
      labelSizePct: data.sightTape && data.sightTape.labelSizePct,
      tickWidthPct: data.sightTape && data.sightTape.tickWidthPct,
    };
    profile.updatedAt = Date.now();
    return {
      v: 1,
      activeProfileId: profile.id,
      profiles: [profile],
    };
  }

  function loadLibrary(customerId) {
    if (!customerId) return null;
    try {
      var raw = readRaw(customerId);
      if (raw) {
        var data = JSON.parse(raw);
        if (data && data.v === 1 && data.profiles && data.profiles.length) return data;
      }
    } catch (e) {
      /* fall through */
    }
    var migrated = migrateFromCalcPresets();
    var lib = migrated || emptyLibrary();
    writeRaw(customerId, JSON.stringify(lib));
    return lib;
  }

  function saveLibrary(customerId, lib) {
    if (!customerId || !lib) return null;
    writeRaw(customerId, JSON.stringify(lib));
    try {
      document.dispatchEvent(new CustomEvent('stc-library-changed', { detail: { library: lib } }));
    } catch (e) {
      /* old browsers */
    }
    return lib;
  }

  function getProfile(lib, id) {
    if (!lib || !lib.profiles) return null;
    for (var i = 0; i < lib.profiles.length; i++) {
      if (lib.profiles[i].id === id) return lib.profiles[i];
    }
    return null;
  }

  function activeProfile(lib) {
    if (!lib) return null;
    return getProfile(lib, lib.activeProfileId) || lib.profiles[0] || null;
  }

  function collectSnapshot(root) {
    if (!root) return null;
    return {
      fps: num(val(root, '[data-stc-fps]')),
      grains: num(val(root, '[data-stc-grains]')),
      gap: num(val(root, '[data-stc-gap]')),
      measuredSpan: num(val(root, '[data-stc-measured-span]')),
      peepToSight: num(val(root, '[data-stc-peep-to-sight]')),
      peepHeight: num(val(root, '[data-stc-peep-height]')),
      sightId: val(root, '[data-stc-sight]') || 'printed',
      sightUnits: readSeg(root, 'data-stc-sight-units') || 'in',
      densityAltFt: num(val(root, '[data-stc-density-alt]')),
      startYd: num(val(root, '[data-stc-start]')),
      endYd: num(val(root, '[data-stc-end]')),
      tapeCount: Number(readSeg(root, 'data-stc-tape-count') || 5),
      tapeMode: readSeg(root, 'data-stc-tape-mode') || 'offset',
      tickStep: Number(readSeg(root, 'data-stc-tick-step') || 2),
      subTickAlign: readSeg(root, 'data-stc-sub-tick-align') || 'center',
      labelStep: Number(readSeg(root, 'data-stc-label-step') || 10),
      splitLabels: readSeg(root, 'data-stc-split-labels') === 'on',
      align: readSeg(root, 'data-stc-align') || 'center',
      tickAlign: readSeg(root, 'data-stc-tick-align') || 'center',
      labelOffsetPx: num(val(root, '[data-stc-label-offset]')),
      labelSizePct: num(val(root, '[data-stc-label-size]')),
      tickWidthPct: num(val(root, '[data-stc-tick-width]')),
      tip: readSeg(root, 'data-stc-tip') || 'field',
      colorBg: val(root, '[data-stc-color-bg]'),
      colorText: val(root, '[data-stc-color-text]'),
      colorTick: val(root, '[data-stc-color-tick]'),
      colorTickFive: val(root, '[data-stc-color-tick-five]'),
      colorTextFive: val(root, '[data-stc-color-text-five]'),
      fiveTickColorOn: readSeg(root, 'data-stc-five-tick-color') === 'on',
      fiveLabelColorOn: readSeg(root, 'data-stc-five-label-color') === 'on',
      printPaper: readSeg(root, 'data-stc-print-paper') || 'portrait',
      printContent: 'full',
      printWidthIn: num(val(root, '[data-stc-print-width]')),
      printStartOffset: num(val(root, '[data-stc-print-start-offset]')),
      printMaxHeight: num(val(root, '[data-stc-print-max-height]')),
      printEccPromised: num(val(root, '[data-stc-print-ecc-promised]')),
      printEccActual: num(val(root, '[data-stc-print-ecc-actual]')),
      bowName: (function () {
        var active = root.querySelector('[data-stc-lib-profiles] [aria-pressed="true"]');
        return active ? String(active.textContent || '').trim() : '';
      })(),
      savedAt: Date.now(),
    };
  }

  var SHARE_ENCODE = {
    fps: 'f',
    grains: 'g',
    gap: 'p',
    measuredSpan: 's',
    peepToSight: 'pts',
    peepHeight: 'ph',
    sightId: 'sid',
    sightUnits: 'su',
    densityAltFt: 'da',
    startYd: 'sy',
    endYd: 'ey',
    tapeCount: 'tc',
    tapeMode: 'tm',
    tickStep: 'ts',
    subTickAlign: 'sta',
    labelStep: 'ls',
    splitLabels: 'sl',
    align: 'al',
    tickAlign: 'ta',
    labelOffsetPx: 'lo',
    labelSizePct: 'lsz',
    tickWidthPct: 'tw',
    tip: 'tip',
    printWidthIn: 'pw',
    printPaper: 'pp',
    printContent: 'pc',
    bowName: 'bn',
  };

  var SHARE_DECODE = {};
  (function () {
    for (var k in SHARE_ENCODE) {
      if (Object.prototype.hasOwnProperty.call(SHARE_ENCODE, k)) {
        SHARE_DECODE[SHARE_ENCODE[k]] = k;
      }
    }
  })();

  function toBase64Url(str) {
    return btoa(unescape(encodeURIComponent(str)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  function fromBase64Url(str) {
    var s = String(str || '')
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    while (s.length % 4) s += '=';
    return decodeURIComponent(escape(atob(s)));
  }

  function compactSharePayload(snapshot) {
    var out = {};
    if (!snapshot) return out;
    for (var key in SHARE_ENCODE) {
      if (!Object.prototype.hasOwnProperty.call(SHARE_ENCODE, key)) continue;
      var val = snapshot[key];
      if (val == null || val === '') continue;
      if (key === 'fps') val = Math.round(val);
      if (key === 'grains') val = Math.round(val);
      if (key === 'splitLabels') val = val ? 1 : 0;
      out[SHARE_ENCODE[key]] = val;
    }
    return out;
  }

  function expandSharePayload(compact) {
    if (!compact || typeof compact !== 'object') return null;
    var out = {};
    for (var shortKey in compact) {
      if (!Object.prototype.hasOwnProperty.call(compact, shortKey)) continue;
      var longKey = SHARE_DECODE[shortKey];
      if (!longKey) continue;
      var val = compact[shortKey];
      if (longKey === 'splitLabels') val = val === 1 || val === true || val === '1';
      if (longKey === 'tapeCount' || longKey === 'tickStep' || longKey === 'labelStep') {
        val = Number(val);
      }
      out[longKey] = val;
    }
    return out;
  }

  function encodeShareParam(snapshot) {
    return toBase64Url(JSON.stringify(compactSharePayload(snapshot)));
  }

  function decodeShareParam(param) {
    if (!param) return null;
    try {
      return expandSharePayload(JSON.parse(fromBase64Url(param)));
    } catch (e) {
      return null;
    }
  }

  function buildShareUrl(snapshot, opts) {
    if (!snapshot || typeof window === 'undefined') return '';
    opts = opts || {};
    var out = new URLSearchParams();
    if (!opts.forPrint) {
      var preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview) out.set('preview_theme_id', preview);
    }
    out.set('stc', encodeShareParam(snapshot));
    var base = String(opts.baseUrl || '').trim();
    if (base) {
      return base.split('#')[0].split('?')[0] + '?' + out.toString();
    }
    var path = window.location.pathname || '/';
    return window.location.origin + path + '?' + out.toString();
  }

  function parseShareFromLocation(search) {
    var param = new URLSearchParams(search || window.location.search).get('stc');
    return decodeShareParam(param);
  }

  function applyShareFromUrl(root) {
    var snapshot = parseShareFromLocation(window.location.search);
    if (!snapshot) return false;
    return applySnapshot(root, snapshot);
  }

  function applySnapshot(root, snapshot) {
    if (!root || !snapshot) return false;
    if (snapshot.fps != null) {
      setInput(root, '[data-stc-fps]', Math.round(snapshot.fps));
      setInput(root, '[data-stc-fps-num]', Math.round(snapshot.fps));
    }
    if (snapshot.grains != null) {
      setInput(root, '[data-stc-grains]', snapshot.grains);
      setInput(root, '[data-stc-grains-num]', snapshot.grains);
    }
    setInput(root, '[data-stc-gap]', snapshot.gap);
    if (snapshot.measuredSpan != null) setInput(root, '[data-stc-measured-span]', snapshot.measuredSpan);
    if (snapshot.peepToSight != null) setInput(root, '[data-stc-peep-to-sight]', snapshot.peepToSight);
    if (snapshot.peepHeight != null) setInput(root, '[data-stc-peep-height]', snapshot.peepHeight);
    if (snapshot.sightId) {
      var sightEl = root.querySelector('[data-stc-sight]');
      if (sightEl) sightEl.value = snapshot.sightId;
    }
    setSeg(root, 'data-stc-sight-units', snapshot.sightUnits || 'in');
    setInput(root, '[data-stc-density-alt]', snapshot.densityAltFt);
    setInput(root, '[data-stc-start]', snapshot.startYd);
    setInput(root, '[data-stc-end]', snapshot.endYd);
    setSeg(root, 'data-stc-tape-count', snapshot.tapeCount);
    setSeg(root, 'data-stc-tape-mode', snapshot.tapeMode);
    setSeg(root, 'data-stc-tick-step', snapshot.tickStep === 1 ? 1 : 2);
    setSeg(root, 'data-stc-sub-tick-align', snapshot.subTickAlign);
    setSeg(root, 'data-stc-label-step', snapshot.labelStep === 5 ? 5 : 10);
    setSeg(root, 'data-stc-split-labels', snapshot.splitLabels ? 'on' : 'off');
    setSeg(root, 'data-stc-align', snapshot.align);
    setSeg(root, 'data-stc-tick-align', snapshot.tickAlign);
    setInput(root, '[data-stc-label-offset]', snapshot.labelOffsetPx);
    setInput(root, '[data-stc-label-size]', snapshot.labelSizePct);
    setInput(root, '[data-stc-tick-width]', snapshot.tickWidthPct);
    setSeg(root, 'data-stc-tip', snapshot.tip || 'field');
    setInput(root, '[data-stc-color-bg]', snapshot.colorBg);
    setInput(root, '[data-stc-color-text]', snapshot.colorText);
    setInput(root, '[data-stc-color-tick]', snapshot.colorTick);
    setInput(root, '[data-stc-color-tick-five]', snapshot.colorTickFive || '#888888');
    setInput(root, '[data-stc-color-text-five]', snapshot.colorTextFive || '#888888');
    setSeg(root, 'data-stc-five-tick-color', snapshot.fiveTickColorOn ? 'on' : 'off');
    setSeg(root, 'data-stc-five-label-color', snapshot.fiveLabelColorOn ? 'on' : 'off');
    setSeg(root, 'data-stc-print-paper', snapshot.printPaper === 'label' ? 'label' : 'portrait');
    setInput(root, '[data-stc-print-width]', snapshot.printWidthIn);
    if (snapshot.printWidthIn != null) {
      setInput(root, '[data-stc-tape-width]', snapshot.printWidthIn);
    }
    setInput(root, '[data-stc-print-start-offset]', snapshot.printStartOffset);
    setInput(root, '[data-stc-print-max-height]', snapshot.printMaxHeight);
    setInput(root, '[data-stc-print-ecc-promised]', snapshot.printEccPromised);
    setInput(root, '[data-stc-print-ecc-actual]', snapshot.printEccActual);
    root.querySelectorAll('input[type="range"], input[type="number"], textarea').forEach(function (el) {
      try {
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (e) {
        /* IE */
      }
    });
    return true;
  }

  function accountConfig(root) {
    var el = root || document.querySelector('[data-stc-calc]');
    var tools = global.TitleXCalcTools;
    if (el && el.hasAttribute('data-stc-accounts-enabled')) {
      return {
        accountsEnabled: el.getAttribute('data-stc-accounts-enabled') === '1',
        loggedIn: el.getAttribute('data-stc-logged-in') === '1',
        registerUrl: tools && tools.buildRegisterUrl ? tools.buildRegisterUrl(el) : '',
        loginUrl: tools && tools.buildLoginUrl ? tools.buildLoginUrl(el) : '',
      };
    }
    if (tools && tools.presetLinkConfig) return tools.presetLinkConfig(root);
    if (!el) {
      return { accountsEnabled: false, loggedIn: false, registerUrl: '', loginUrl: '' };
    }
    return {
      accountsEnabled: el.getAttribute('data-stc-accounts-enabled') === '1',
      loggedIn: el.getAttribute('data-stc-logged-in') === '1',
      registerUrl: tools && tools.buildRegisterUrl ? tools.buildRegisterUrl(el) : '',
      loginUrl: tools && tools.buildLoginUrl ? tools.buildLoginUrl(el) : '',
    };
  }

  function applyAuthProbe(root, probe) {
    if (!root || !probe) return { loggedIn: false, verified: false };
    var loggedIn = probe.getAttribute('data-stc-logged-in') === '1';
    var customerId = probe.getAttribute('data-stc-customer-id');
    var email = probe.getAttribute('data-stc-customer-email') || '';
    if (loggedIn && customerId) {
      applyAuthToRoot(root, true, customerId, email, false);
      clearStaleSession(root);
      writeCustomerCookie({ id: customerId, email: email });
      try {
        sessionStorage.removeItem(REAUTH_ATTEMPT_KEY);
      } catch (e) {
        /* blocked */
      }
      return { loggedIn: true, verified: true };
    }
    var cookie = readCustomerCookie();
    if (cookie && cookie.id) {
      markStaleSession(root, cookie);
      return { loggedIn: false, verified: true, staleCookie: true };
    }
    clearCustomerCookie();
    clearStaleSession(root);
    applyAuthToRoot(root, false, null, null, false);
    return { loggedIn: false, verified: true };
  }

  function tryRestoreSession(root) {
    stashPendingSnapshot(root);
    try {
      sessionStorage.setItem(REAUTH_ATTEMPT_KEY, String(Date.now()));
    } catch (e) {
      /* blocked */
    }
    redirectToAccount(root, false);
  }

  function maybeAutoRestoreSession(root, result) {
    return false;
  }

  function refreshAuthState(root) {
    if (!root || root.getAttribute('data-stc-accounts-enabled') !== '1') {
      return Promise.resolve({ loggedIn: root && root.getAttribute('data-stc-logged-in') === '1', verified: true });
    }
    syncCookieFromRoot(root);
    var sectionKey = root.getAttribute('data-stc-auth-section') || 'stc-auth-probe';
    var params = new URLSearchParams();
    params.set('sections', sectionKey);
    var previewTheme = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewTheme) params.set('preview_theme_id', previewTheme);
    var path = window.location.pathname || '/';
    return fetch(path + '?' + params.toString(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (data) {
        if (!data || !data[sectionKey]) {
          var cookie = readCustomerCookie();
          if (cookie && cookie.id && root.getAttribute('data-stc-auth-optimistic') === '1') {
            return { loggedIn: true, verified: false, fromCookie: true };
          }
          return { loggedIn: false, verified: false };
        }
        var doc = new DOMParser().parseFromString(data[sectionKey], 'text/html');
        var probe = doc.querySelector('[data-stc-auth-probe]');
        if (!probe) return { loggedIn: false, verified: false };
        return applyAuthProbe(root, probe);
      })
      .catch(function () {
        var cookie = readCustomerCookie();
        if (cookie && cookie.id) {
          applyAuthToRoot(root, true, cookie.id, cookie.email || '', true);
          return { loggedIn: true, verified: false, fromCookie: true };
        }
        return { loggedIn: false, verified: false };
      });
  }

  function canPrint(root) {
    var cfg = accountConfig(root);
    if (!cfg.accountsEnabled) return true;
    return cfg.loggedIn;
  }

  function redirectToAccount(root, preferRegister) {
    var tools = global.TitleXCalcTools;
    var url = '';
    if (tools) {
      url = preferRegister === true ? tools.buildRegisterUrl(root) : tools.buildLoginUrl(root);
    }
    if (!url) {
      var cfg = accountConfig(root);
      url = preferRegister === true ? cfg.registerUrl || cfg.loginUrl : cfg.loginUrl || cfg.registerUrl;
    }
    if (url) window.location.href = url;
  }

  function stashPendingSnapshot(root) {
    try {
      sessionStorage.setItem(
        PENDING_KEY,
        JSON.stringify({ snapshot: collectSnapshot(root), returnUrl: location.pathname + location.search })
      );
    } catch (e) {
      /* blocked */
    }
  }

  function tryPendingSnapshot(root) {
    try {
      var raw = sessionStorage.getItem(PENDING_KEY);
      if (!raw) return false;
      sessionStorage.removeItem(PENDING_KEY);
      var payload = JSON.parse(raw);
      if (!payload || !payload.snapshot) return false;
      return applySnapshot(root, payload.snapshot);
    } catch (e) {
      return false;
    }
  }

  function formatVersionLabel(entry, index) {
    if (entry.label) return entry.label;
    var d = new Date(entry.savedAt || Date.now());
    return (
      'v' +
      (index + 1) +
      ' · ' +
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    );
  }

  global.TitleXSightTapeLibrary = {
    MAX_HISTORY: MAX_HISTORY,
    loadLibrary: loadLibrary,
    saveLibrary: saveLibrary,
    activeProfile: activeProfile,
    getProfile: getProfile,
    collectSnapshot: collectSnapshot,
    applySnapshot: applySnapshot,
    buildShareUrl: buildShareUrl,
    applyShareFromUrl: applyShareFromUrl,
    parseShareFromLocation: parseShareFromLocation,
    accountConfig: accountConfig,
    refreshAuthState: refreshAuthState,
    readCustomerCookie: readCustomerCookie,
    applyOptimisticAuthFromCookie: applyOptimisticAuthFromCookie,
    syncCookieFromRoot: syncCookieFromRoot,
    tryRestoreSession: tryRestoreSession,
    maybeAutoRestoreSession: maybeAutoRestoreSession,
    clearCustomerCookie: clearCustomerCookie,
    canPrint: canPrint,
    redirectToAccount: redirectToAccount,
    stashPendingSnapshot: stashPendingSnapshot,
    tryPendingSnapshot: tryPendingSnapshot,
    formatVersionLabel: formatVersionLabel,
    uid: uid,

    createProfile: function (customerId, name) {
      var lib = loadLibrary(customerId);
      if (!lib || lib.profiles.length >= MAX_PROFILES) return null;
      var profile = defaultProfile(name || 'New bow');
      lib.profiles.push(profile);
      lib.activeProfileId = profile.id;
      saveLibrary(customerId, lib);
      return profile;
    },

    deleteProfile: function (customerId, profileId) {
      var lib = loadLibrary(customerId);
      if (!lib || lib.profiles.length <= 1) return null;
      lib.profiles = lib.profiles.filter(function (p) {
        return p.id !== profileId;
      });
      if (lib.activeProfileId === profileId) lib.activeProfileId = lib.profiles[0].id;
      saveLibrary(customerId, lib);
      return lib;
    },

    setActiveProfile: function (customerId, profileId) {
      var lib = loadLibrary(customerId);
      if (!lib || !getProfile(lib, profileId)) return null;
      lib.activeProfileId = profileId;
      saveLibrary(customerId, lib);
      return lib;
    },

    renameProfile: function (customerId, profileId, name) {
      var lib = loadLibrary(customerId);
      var profile = getProfile(lib, profileId);
      if (!profile || !name) return null;
      profile.name = String(name).trim().slice(0, 48) || profile.name;
      profile.updatedAt = Date.now();
      saveLibrary(customerId, lib);
      return profile;
    },

    saveCurrent: function (customerId, root, opts) {
      opts = opts || {};
      var lib = loadLibrary(customerId);
      if (!lib) return null;
      var profile = activeProfile(lib);
      if (!profile) return null;
      var snapshot = collectSnapshot(root);
      if (!snapshot) return null;

      if (profile.snapshot) {
        profile.history.unshift({
          id: uid(),
          savedAt: profile.snapshot.savedAt || Date.now(),
          label: opts.versionLabel || '',
          snapshot: profile.snapshot,
        });
        if (profile.history.length > MAX_HISTORY) profile.history.length = MAX_HISTORY;
      }

      profile.snapshot = snapshot;
      profile.updatedAt = Date.now();
      saveLibrary(customerId, lib);

      if (global.TitleXCalcPresets && global.TitleXCalcPresets.save) {
        global.TitleXCalcPresets.save({
          name: profile.name,
          bow: snapshot.fps != null ? { chrono: Math.round(snapshot.fps) } : undefined,
          arrow: { grains: snapshot.grains, tip: snapshot.tip },
          sightTape: {
            gap2060: snapshot.gap,
            peepToSight: snapshot.peepToSight,
            peepHeight: snapshot.peepHeight,
            sightId: snapshot.sightId,
            sightUnits: snapshot.sightUnits,
            densityAltFt: snapshot.densityAltFt,
            startYd: snapshot.startYd,
            endYd: snapshot.endYd,
            tickStep: snapshot.tickStep,
            align: snapshot.align,
            tickAlign: snapshot.tickAlign,
            labelOffsetPx: snapshot.labelOffsetPx,
            labelSizePct: snapshot.labelSizePct,
            tickWidthPct: snapshot.tickWidthPct,
            tip: snapshot.tip,
          },
        });
      }
      return profile;
    },

    restoreVersion: function (customerId, profileId, versionId) {
      var lib = loadLibrary(customerId);
      var profile = getProfile(lib, profileId);
      if (!profile) return null;
      var entry = null;
      for (var i = 0; i < profile.history.length; i++) {
        if (profile.history[i].id === versionId) {
          entry = profile.history[i];
          break;
        }
      }
      if (!entry || !entry.snapshot) return null;
      if (profile.snapshot) {
        profile.history.unshift({
          id: uid(),
          savedAt: profile.snapshot.savedAt || Date.now(),
          label: 'Before restore',
          snapshot: profile.snapshot,
        });
        if (profile.history.length > MAX_HISTORY) profile.history.length = MAX_HISTORY;
      }
      profile.snapshot = JSON.parse(JSON.stringify(entry.snapshot));
      profile.snapshot.savedAt = Date.now();
      profile.updatedAt = Date.now();
      saveLibrary(customerId, lib);
      return profile.snapshot;
    },
  };
})(typeof window !== 'undefined' ? window : this);
